// Reading what the GAMMIS portal said about a member's coverage.
//
// Eligibility has been checked by hand for years: a coordinator opens the
// GAMMIS portal, looks the member up, and pastes the result into
// `clients.eligibility_notes`. 73 clients carry one. The paste is tab-separated
// and structurally consistent, so it is real data wearing the clothes of free
// text — coverage end dates, aid categories and managed-care enrolment that
// nothing has ever been able to query.
//
// This parses it. That matters twice over: it turns the existing history into
// something answerable, and it is the same shape a 271 will produce, so the
// automated path and the manual one describe coverage the same way.

import { destinationForPayer } from './serviceModel.js';

/** What a check concluded, independent of how it was obtained. */
export const RESULTS = ['active', 'inactive', 'pending', 'not_found', 'error', 'unknown'];

const STATUS_TO_RESULT = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  TERMINATED: 'inactive',
  CANCELLED: 'inactive',
  CANCELED: 'inactive',
  PENDING: 'pending',
  DENIED: 'inactive',
};

/** Georgia Families care management organisations, as the portal names them. */
const CMOS = [
  ['AMERIGROUP', 'Amerigroup Community Care'],
  ['CARESOURCE', 'CareSource'],
  ['PEACH STATE', 'Peach State Health Plan'],
  ['WELLCARE', 'WellCare'],
  ['WELLPOINT', 'Wellpoint'],
];

/**
 * A care management organisation's name as CAERA writes it, however the
 * source spelled it: the portal says "AMERIGROUP COMMUNITY CARE - ATLANTA", a
 * 271 says "CARESOURCE", the history says "CareSource". Unknown names pass
 * through unchanged rather than being guessed at.
 */
export const normaliseCmo = (name) => {
  const raw = String(name ?? '').trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  return CMOS.find(([needle]) => upper.includes(needle))?.[1] || raw;
};

/** MM/DD/YYYY as the portal writes it → YYYY-MM-DD. */
export const usDateToIso = (v) => {
  const m = String(v ?? '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, mm, dd, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
};

/** `medicaid_eligible` has always been free text. Map it to a result. */
export const resultFromEligibleFlag = (v) => {
  const s = String(v ?? '').trim().toUpperCase();
  if (s === 'YES' || s === 'Y' || s === 'ACTIVE') return 'active';
  if (s === 'NO' || s === 'N') return 'inactive';
  if (s === 'PENDING') return 'pending';
  return 'unknown';
};

/**
 * Parse a pasted GAMMIS eligibility result.
 *
 * The row that matters looks like:
 *
 *   Active ⇥ 30 - Health Plan Benefit Coverage ⇥ 09/01/2026 ⇥ 09/30/2026
 *          ⇥ MC - Medicaid ⇥ 105 - LIM - Child ⇥ MEDICAID
 *
 * A header row may precede it and a managed-care line may precede that. Five
 * of the 73 stored notes have no such row at all — a free-text remark rather
 * than a paste — and those come back `parsed: false` with the text intact,
 * because inventing structure for them would be worse than admitting there
 * is none.
 */
export const parseGammisEligibilityNote = (text) => {
  const raw = String(text ?? '');
  const empty = {
    parsed: false, result: 'unknown', status: null,
    serviceTypeCode: null, coverageStart: null, coverageEnd: null,
    insuranceType: null, aidCategory: null, specialNotes: null,
    managedCareOrg: null, raw: raw.trim() || null,
  };
  if (!raw.trim()) return { ...empty, raw: null };

  const upper = raw.toUpperCase();
  const managedCareOrg = CMOS.find(([needle]) => upper.includes(needle))?.[1] || null;

  // The data row: a status word followed by tab-separated columns. The header
  // row starts with "Status", which is why the status words are matched
  // explicitly rather than "first row with tabs".
  const row = raw
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+$/, ''))
    .find((l) => /^\s*(Active|Inactive|Terminated|Cancelled|Canceled|Pending|Denied)\s*\t/i.test(l));

  if (!row) return { ...empty, managedCareOrg };

  const cols = row.split('\t').map((c) => c.trim());
  const status = cols[0] || null;

  return {
    parsed: true,
    result: STATUS_TO_RESULT[String(status).toUpperCase()] || 'unknown',
    status,
    serviceTypeCode: cols[1] || null,
    coverageStart: usDateToIso(cols[2]),
    coverageEnd: usDateToIso(cols[3]),
    insuranceType: cols[4] || null,
    aidCategory: cols[5] || null,
    specialNotes: cols[6] || null,
    managedCareOrg,
    raw: raw.trim(),
  };
};

/**
 * What changed between two checks, in words a digest can print.
 *
 * Only differences a person would act on: coverage ending or starting, the
 * result flipping, or a move between fee-for-service and a managed-care plan.
 * An aid-category change on otherwise identical coverage is noise.
 */
export const eligibilityChanges = (previous, current) => {
  if (!previous || !current) return [];
  const changes = [];

  if (previous.result !== current.result) {
    changes.push(`coverage went from ${previous.result} to ${current.result}`);
  }
  if ((previous.coverageEnd || null) !== (current.coverageEnd || null)) {
    changes.push(current.coverageEnd
      ? `coverage now ends ${current.coverageEnd}${previous.coverageEnd ? ` (was ${previous.coverageEnd})` : ''}`
      : 'coverage end date has been removed');
  }
  const wasCmo = previous.managedCareOrg || null;
  const isCmo = current.managedCareOrg || null;
  if (wasCmo !== isCmo) {
    if (isCmo && !wasCmo)      changes.push(`now enrolled with ${isCmo}`);
    else if (!isCmo && wasCmo) changes.push(`no longer enrolled with ${wasCmo}`);
    else                       changes.push(`moved from ${wasCmo} to ${isCmo}`);
  }
  return changes;
};

// ── The automated path ──────────────────────────────────────────────────────
//
// Everything above this line reads what a person pasted out of the GAMMIS
// portal. Below it is the same information arriving as a 271, which is the
// point of enrolling: 29 clients checked by hand, one at a time, becomes one
// file a week.
//
// The two paths converge here deliberately. `parse271` was written to produce
// the same shape `parseGammisEligibilityNote` does, so a check recorded
// automatically and one recorded by hand are the same row with a different
// `channel`, and the history reads as one story rather than two.

/** How a `clients.medicaid_eligible` cache value reads, given a result. */
const ELIGIBLE_FLAG = {
  active: 'Yes',
  inactive: 'No',
  pending: 'Pending',
  not_found: 'No',
};

/**
 * One `eligibility_checks` row from one member result.
 *
 * `raw_response` holds the whole 271, not this member's slice: a file that
 * parsed wrongly is diagnosed from what actually arrived, and cutting it up
 * first destroys the only evidence of how.
 */
export const checkRowFromMember = (member, {
  clientId = null, clientRef = null,
  trigger = 'weekly', channel = 'edi_270',
  controlNumber = null, raw = null, checkedAt = new Date(),
} = {}) => ({
  client_id: clientId,
  client_ref: clientRef,
  checked_at: checkedAt.toISOString(),
  trigger,
  channel,
  member_id_sent: member.memberId || null,
  dob_sent: member.dob || null,
  result: RESULTS.includes(member.result) ? member.result : 'unknown',
  status_text: member.status || null,
  service_type_code: member.serviceTypeCode || null,
  coverage_start: member.coverageStart || null,
  coverage_end: member.coverageEnd || null,
  insurance_type: member.insuranceType || null,
  aid_category: member.aidCategory || null,
  special_notes: member.specialNotes || null,
  managed_care_org: member.managedCareOrg || null,
  coverage_end_is_plan_end: !!member.coverageEndIsPlanEnd,
  reject_code: member.rejectCode || null,
  reject_reason: member.rejectReason || null,
  raw_response: raw,
  control_number: controlNumber,
});

/**
 * The columns on `clients` that mirror the newest check.
 *
 * The history table is the record; these are a cache, so every screen that
 * already reads them keeps working without knowing the history exists.
 *
 * A `not_found` or an `error` does NOT overwrite the cached answer. "We could
 * not ask" is not the same as "there is no coverage", and letting a failed
 * lookup blank a client's eligibility would turn a transmission problem into
 * what looks like a lapse.
 */
export const clientCacheUpdate = (member, { checkedAt = new Date() } = {}) => {
  const flag = ELIGIBLE_FLAG[member.result];
  if (!flag || member.result === 'error' || member.result === 'unknown') return null;
  if (member.result === 'not_found') return null;

  return {
    medicaid_eligible: flag,
    eligibility_checked_date: checkedAt.toISOString().slice(0, 10),
    eligibility_checked_by: 'Automatic 270/271',
  };
};

/**
 * Match a 271 member back to the client it answers.
 *
 * By Medicaid ID, which is what was sent and what comes back. Name is a
 * fallback only where the ID is absent — a 271 rejected with AAA*72 (invalid
 * member id) may return no ID at all, and dropping those would hide exactly
 * the case worth seeing.
 */
export const matchMemberToClient = (member, clients = []) => {
  const id = String(member.memberId || '').replace(/\D/g, '');
  if (id) {
    const byId = clients.find((c) => String(c.medicaid_id || '').replace(/\D/g, '') === id);
    if (byId) return byId;
  }
  const last = String(member.lastName || '').trim().toLowerCase();
  const first = String(member.firstName || '').trim().toLowerCase();
  if (!last) return null;
  const named = clients.filter((c) =>
    String(c.last_name || '').trim().toLowerCase() === last &&
    (!first || String(c.first_name || '').trim().toLowerCase() === first));
  // Two clients with the same name is a real possibility and guessing between
  // them is worse than reporting the member unmatched.
  return named.length === 1 ? named[0] : null;
};

/** The shape eligibilityChanges() compares, read off a stored row. */
export const comparableFromRow = (row) => row && ({
  result: row.result,
  coverageStart: row.coverage_start,
  coverageEnd: row.coverage_end,
  managedCareOrg: row.managed_care_org,
});

// ── What the baseline acts on (decided 2026-10-24) ──────────────────────────
//
// Checked by moment, not by calendar: every child on the 1st of the month,
// and each billing run's children before the run is filed. A check that
// changes nothing does not add a row — it confirms the newest one — so the
// history is a list of things that happened, not 1,500 "still covered" rows a
// year. Exceptions reach people through the Daily Digest and pre-flight; the
// rest is on the child's own Coverage card.

/** How a check came about, on the file name, so a 271 can say which 270 it answers. */
export const ELIGIBILITY_FILE_TAG = { monthly: 'M', pre_submission: 'P', manual: 'H' };

/** Which trigger a 271 answers, from the 270 name GAMMIS echoes inside its own. */
export const triggerFromFileName = (name) => {
  const m = String(name || '').toUpperCase().match(/(?:^|[^A-Z0-9])270-([A-Z])-/);
  const tag = m?.[1];
  return Object.keys(ELIGIBILITY_FILE_TAG).find((k) => ELIGIBILITY_FILE_TAG[k] === tag) || 'monthly';
};

/** When a check was last known to hold: its newest confirmation, else when it was made. */
export const lastConfirmed = (row) => (row ? (row.last_confirmed_at || row.checked_at || null) : null);

/**
 * Insert a new history row, or confirm the newest one?
 *
 * A new row when there is nothing to compare with, or when something a person
 * would act on changed (eligibilityChanges). Otherwise the answer is the same
 * one again, and the newest row is confirmed instead.
 */
export const eligibilityDecision = (previousRow, member) => {
  if (!previousRow) return { action: 'insert', changes: [] };
  const changes = eligibilityChanges(comparableFromRow(previousRow), member);
  return changes.length ? { action: 'insert', changes } : { action: 'confirm', changes: [] };
};

/** The columns that confirm a row. */
export const confirmationUpdate = (previousRow, at = new Date()) => ({
  last_confirmed_at: at.toISOString(),
  confirmations: (Number(previousRow?.confirmations) || 0) + 1,
});

const CARESOURCE = 'CareSource';
const isoDay = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : String(d || '').slice(0, 10));
const daysBetween = (fromIso, toIso) =>
  Math.round((Date.parse(`${toIso}T12:00:00Z`) - Date.parse(`${fromIso}T12:00:00Z`)) / 86400000);
const firstOfMonth = (iso) => `${iso.slice(0, 7)}-01`;

/**
 * Where GAMMIS says the child's coverage sits, against where CAERA files.
 *
 * The most useful thing a 271 says. A GAPP or Medicaid child whom GAMMIS shows
 * enrolled with a care management organisation will have GAMMIS claims denied
 * — the CMO carries the coverage (G012 was enrolled with CareSource and CAERA
 * never knew). And a child CAERA bills to CareSource whom GAMMIS shows without
 * CareSource is billed to the wrong place.
 */
export const payerDrift = (payer, check) => {
  if (!check || check.result !== 'active') return null;
  const cmo = check.managed_care_org || null;
  if (destinationForPayer(payer) === 'gammis') {
    return cmo ? `GAMMIS shows enrolment with ${cmo}; claims filed with GAMMIS would deny. Check the payer.` : null;
  }
  if (payer === 'caresource' && cmo !== CARESOURCE) {
    return `billed to CareSource, but GAMMIS shows ${cmo ? `enrolment with ${cmo}` : 'no managed-care plan'}.`;
  }
  return null;
};

/**
 * Days until a real end of coverage, or null.
 *
 * Only a plan end date (DTP*347) counts. The coverage dates on a portal paste,
 * and the eligibility period on a 271, echo the date or month that was asked
 * about — on 24 October 2026 every receiving client's latest check "ended" on
 * the day it was made — so reading them as an end of coverage would warn about
 * every child, every time.
 */
export const daysToPlanEnd = (check, today = new Date()) => {
  if (!check?.coverage_end_is_plan_end || !check.coverage_end || check.result !== 'active') return null;
  const days = daysBetween(isoDay(today), check.coverage_end);
  return days >= 0 ? days : null;
};

export const RENEWAL_WARN_DAYS = 45;
export const RENEWAL_URGENT_DAYS = 30;

/**
 * Everything about a child's coverage someone needs to act on, as of a date.
 *
 * `asOf` is today for the Digest and the first day of the service week for
 * pre-flight. `unconfirmed` — no confirmation since the first of asOf's month —
 * is included only when asked for: it is the pre-flight question "did anyone
 * ask about this month?", and it would list every child on every Digest until
 * the monthly check is switched on.
 *
 * @param {{ payer?: string, latest?: any, asOf?: Date|string, includeUnconfirmed?: boolean }} [p]
 * @returns {{ kind: string, text: string, days?: number }[]}
 */
export const coverageProblems = ({ payer, latest, asOf = new Date(), includeUnconfirmed = false } = {}) => {
  const out = [];
  const day = isoDay(asOf);
  if (!latest) {
    if (includeUnconfirmed) out.push({ kind: 'unconfirmed', text: 'no eligibility check on record' });
    return out;
  }
  if (latest.result === 'inactive') out.push({ kind: 'inactive', text: 'GAMMIS says coverage is inactive' });
  if (latest.result === 'not_found') {
    out.push({ kind: 'not_found', text: `GAMMIS could not find the member${latest.reject_reason ? ` (${latest.reject_reason})` : ''}; check the Medicaid ID and date of birth` });
  }
  const drift = payerDrift(payer, latest);
  if (drift) out.push({ kind: 'payer_drift', text: drift });
  const days = daysToPlanEnd(latest, asOf);
  if (days !== null && days <= RENEWAL_WARN_DAYS) {
    out.push({ kind: 'ending_soon', days, text: `coverage ends ${latest.coverage_end} (${days} day${days === 1 ? '' : 's'})` });
  }
  if (includeUnconfirmed) {
    const confirmed = isoDay(lastConfirmed(latest));
    if (!confirmed || confirmed < firstOfMonth(day)) {
      out.push({ kind: 'unconfirmed', text: `not confirmed since ${confirmed || 'never'}` });
    }
  }
  return out;
};

/**
 * Twelve months of coverage for the Coverage card, oldest first.
 *
 * A history row stands from when it was made until it was last confirmed, so
 * every month that span touches takes its result. A month nothing touched is
 * 'unchecked' — honestly unknown, not assumed covered. Where two rows touch a
 * month the later one wins.
 *
 * @returns {{ month: 'YYYY-MM', result: string }[]}
 */
export const coverageMonths = (rows = [], today = new Date(), count = 12) => {
  const end = isoDay(today).slice(0, 7);
  const months = [];
  let [y, m] = end.split('-').map(Number);
  for (let i = 0; i < count; i++) {
    months.unshift(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1; if (m === 0) { m = 12; y -= 1; }
  }
  const byMonth = Object.fromEntries(months.map((k) => [k, 'unchecked']));
  const sorted = [...(rows || [])].filter((r) => r?.checked_at)
    .sort((a, b) => String(a.checked_at).localeCompare(String(b.checked_at)));
  for (const r of sorted) {
    const from = isoDay(r.checked_at).slice(0, 7);
    const to = isoDay(lastConfirmed(r)).slice(0, 7);
    for (const k of months) if (k >= from && k <= to) byMonth[k] = r.result || 'unknown';
  }
  return months.map((k) => ({ month: k, result: byMonth[k] }));
};
